import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { locField, REACTION_EMOJI, REACTION_ORDER } from "@/lib/i18nField";
import { formatDate } from "@/lib/format";
import AuthorBadges from "@/components/community/AuthorBadges";
import { MessageSquare, Pin, ShieldCheck, User } from "lucide-react";

const TYPE_COLORS = {
  question: "text-sky-300 border-sky-800/40 bg-sky-950/30",
  photo: "text-fuchsia-300 border-fuchsia-800/40 bg-fuchsia-950/30",
  carpool: "text-emerald-300 border-emerald-800/40 bg-emerald-950/30",
  recommendation: "text-amber-300 border-amber-800/40 bg-amber-950/30",
  discussion: "text-stone-300 border-[#272A34] bg-[#181A20]",
  event: "text-indigo-300 border-indigo-800/40 bg-indigo-950/30",
};

export default function PostCard({ post }) {
  const { lang, t } = useLang();
  const title = locField(post.title, lang);
  const content = locField(post.content, lang);
  const official = post.is_official || post.author_role === "official";
  const totalReacts = post.reaction_total || 0;

  return (
    <Link to={`/community/post/${post.id}`} data-testid={`post-card-${post.id}`}
      className="block bg-[#121418] border border-[#272A34] hover:border-amber-500/40 rounded-lg p-4 sm:p-5 transition-all">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border ${TYPE_COLORS[post.type] || TYPE_COLORS.discussion}`}>{t(`com_type_${post.type}`)}</span>
        {post.pinned && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-amber-400"><Pin className="w-3 h-3" /> {t("com_pinned")}</span>}
        {post.original_language && post.original_language !== "other" && <span className="text-[10px] font-mono uppercase tracking-wider text-stone-600">{post.original_language}</span>}
      </div>
      {title && <h3 className="font-serif text-lg text-stone-100 mb-1.5 leading-snug">{title}</h3>}
      {content && <p className="text-sm text-stone-400 line-clamp-3 mb-3 whitespace-pre-line">{content}</p>}
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="flex items-center gap-2 min-w-0">
          {post.author_picture ? <img src={post.author_picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : <span className="w-6 h-6 rounded-full bg-amber-900/40 flex items-center justify-center"><User className="w-3.5 h-3.5 text-amber-400" /></span>}
          <span className="text-stone-400 truncate flex items-center gap-1">
            {post.author_name || "—"}
            {official && !post.author_badges && <ShieldCheck className="w-3.5 h-3.5 text-amber-500" title={t("com_role_official")} />}
          </span>
          <AuthorBadges badges={post.author_badges} />
          <span className="text-stone-600 font-mono hidden sm:inline">· {formatDate(post.created_at, lang)}</span>
        </span>
        <span className="flex items-center gap-3 text-stone-500 shrink-0">
          {totalReacts > 0 && <span className="flex items-center gap-0.5">{REACTION_ORDER.filter((e) => (post.reaction_counts || {})[e] > 0).slice(0, 3).map((e) => <span key={e}>{REACTION_EMOJI[e]}</span>)} {totalReacts}</span>}
          <span className="flex items-center gap-1 font-mono"><MessageSquare className="w-3.5 h-3.5 text-amber-600/80" /> {post.comment_count || 0}</span>
        </span>
      </div>
    </Link>
  );
}
